import { all, call, cancel, cancelled, fork, put, select } from 'redux-saga/effects'
import chunk from 'lodash/chunk'
import moment from 'moment'
import { spotifyUri } from 'helpers'
import { Scope, SpotifyEntity } from 'enums'
import {
  getAlbumsTracks,
  createPlaylist,
  addTracksToPlaylist,
  getUserSavedPlaylistsPage,
  clearPlaylist,
} from 'api'
import { AuthError, getAuthData, getPlaylistScope } from 'auth'
import {
  getFollowedArtistIds,
  getLastPlaylistsRefresh,
  getPlaylistForm,
  getPlaylists,
  getReleases,
  getUser,
} from 'state/selectors'
import {
  createPlaylistError,
  createPlaylistFinished,
  createPlaylistStart,
  loadPlaylists,
  loadPlaylistsError,
  loadPlaylistsFinished,
  loadPlaylistsStart,
  showErrorMessage,
  updatePlaylistError,
  updatePlaylistFinished,
  updatePlaylistStart,
} from 'state/actions'
import { authorize } from './auth'
import { withTitle } from './helpers'
import { getAllPaged, setupWorkers } from './request'

const { TRACK } = SpotifyEntity

/**
 * Playlist creation saga
 *
 * @param {CreatePlaylistAction} action
 */
export function* createPlaylistSaga(action) {
  const abortController = new AbortController()

  try {
    /** @type {ReturnType<typeof getPlaylistForm>} */
    const { isPublic } = yield select(getPlaylistForm)
    /** @type {ReturnType<typeof getPlaylistScope>} */
    const scope = yield call(getPlaylistScope, Boolean(isPublic))

    /** @type {ReturnType<typeof withTitle>} */
    const titled = yield call(
      withTitle,
      'Creating playlist...',
      createPlaylistMainSaga,
      abortController.signal
    )
    /** @type {ReturnType<typeof authorize>} */
    const authorized = yield call(authorize, action, [scope], titled)

    yield call(authorized)
  } catch (error) {
    if (error instanceof Error) {
      yield put(showErrorMessage(error.message))
    }
    yield put(createPlaylistError())
  } finally {
    // @ts-expect-error
    if (yield cancelled()) abortController.abort()
  }
}

/**
 * Playlist creation main saga
 *
 * @param {AbortSignal} signal
 */
function* createPlaylistMainSaga(signal) {
  yield put(createPlaylistStart())

  /** @type {ReturnType<typeof getAuthData>} */
  const { token } = yield call(getAuthData)
  if (!token) throw new AuthError('Missing token')

  /** @type {ReturnType<typeof getUser>} */
  const user = yield select(getUser)
  if (!user) throw new AuthError('Missing user')

  /** @type {ReturnType<typeof getPlaylistForm>} */
  const form = yield select(getPlaylistForm)
  /** @type {GeneratorReturnType<ReturnType<typeof getReleasesTrackUris>>} */
  const trackUris = yield call(getReleasesTrackUris, signal, Boolean(form.followedArtistsOnly))
  if (trackUris.length === 0) throw new Error('No tracks to export')

  /** @type {SpotifyPlaylist | null} */
  let firstPlaylist = null

  for (const [part, playlistTrackUrisChunk] of chunk(trackUris, 9500).entries()) {
    const name = part > 0 ? `${form.name} (${part + 1})` : form.name
    /** @type {Await<ReturnType<typeof createPlaylist>>} */
    const playlist = yield call(createPlaylist, token, user.id, { ...form, name }, signal)

    if (!firstPlaylist) firstPlaylist = playlist

    for (const trackUrisChunk of chunk(playlistTrackUrisChunk, 100)) {
      yield call(addTracksToPlaylist, token, playlist.id, trackUrisChunk, signal)
    }
  }

  if (firstPlaylist) {
    yield put(createPlaylistFinished({ id: firstPlaylist.id, name: firstPlaylist.name }))
  }
}

/**
 * @param {UpdatePlaylistAction} action
 */
export function* updatePlaylistSaga(action) {
  const abortController = new AbortController()

  try {
    /** @type {Scope[]} */
    const scopes = [Scope.PLAYLIST_MODIFY_PRIVATE, Scope.PLAYLIST_MODIFY_PUBLIC]

    /** @type {ReturnType<typeof withTitle>} */
    const titled = yield call(
      withTitle,
      'Updating playlist...',
      updatePlaylistMainSaga,
      action,
      abortController.signal
    )
    /** @type {ReturnType<typeof authorize>} */
    const authorized = yield call(authorize, action, scopes, titled)

    yield call(authorized)
  } catch (error) {
    if (error instanceof Error) {
      yield put(showErrorMessage(error.message))
    }
    yield put(updatePlaylistError())
  } finally {
    // @ts-expect-error
    if (yield cancelled()) abortController.abort()
  }
}

/**
 * @param {UpdatePlaylistAction} action
 * @param {AbortSignal} signal
 */
function* updatePlaylistMainSaga(action, signal) {
  yield put(updatePlaylistStart())

  const { playlist, strategy, followedArtistsOnly } = action.payload
  /** @type {ReturnType<typeof getAuthData>} */
  const { token } = yield call(getAuthData)
  if (!token) throw new AuthError('Missing token')

  /** @type {GeneratorReturnType<ReturnType<typeof getReleasesTrackUris>>} */
  const trackUris = yield call(getReleasesTrackUris, signal, followedArtistsOnly)
  if (trackUris.length === 0) throw new Error('No tracks to export')

  if (strategy === 'replace') {
    yield call(clearPlaylist, token, playlist.id, signal)
  }

  for (const trackUrisChunk of chunk(trackUris, 100)) {
    yield call(addTracksToPlaylist, token, playlist.id, trackUrisChunk, signal)
  }

  yield put(updatePlaylistFinished(playlist))
}

/**
 * @param {AbortSignal} signal
 * @param {boolean} followedArtistsOnly
 */
function* getReleasesTrackUris(signal, followedArtistsOnly) {
  /** @type {ReturnType<typeof getAuthData>} */
  const { token } = yield call(getAuthData)
  if (!token) throw new AuthError('Missing token')

  /** @type {ReturnType<typeof getReleases>} */
  const releases = yield select(getReleases)

  const albumIds = releases.reduce(
    (ids, { albums }) => ids.concat(albums.map((album) => album.id)),
    /** @type {string[]} */ ([])
  )

  const tracksCalls = chunk(albumIds, 20).map((albumIdsChunk) =>
    call(getAlbumsTracks, token, albumIdsChunk, signal)
  )

  /** @type {Await<ReturnType<typeof getAlbumsTracks>>[]} */
  const tracksChunks = yield all(tracksCalls)
  let tracks = tracksChunks.flat()

  if (followedArtistsOnly) {
    /** @type {ReturnType<typeof getFollowedArtistIds>} */
    const followedArtistIds = yield select(getFollowedArtistIds)
    tracks = tracks.filter((track) =>
      track.artists.some((artist) => followedArtistIds.has(artist.id))
    )
  }

  return tracks.map((track) => spotifyUri(track.id, TRACK))
}

export function* refreshPlaylistsSaga() {
  /** @type {ReturnType<typeof getPlaylists>} */
  const playlists = yield select(getPlaylists)
  /** @type {ReturnType<typeof getLastPlaylistsRefresh>} */
  const lastRefresh = yield select(getLastPlaylistsRefresh)

  const shouldRefresh = () => {
    if (playlists.length === 0) return true
    if (!lastRefresh) return true
    if (moment(lastRefresh).isBefore(moment().subtract(1, 'hour'))) return true
    return false
  }

  if (shouldRefresh()) yield put(loadPlaylists())
}

/**
 * @param {LoadPlaylistsAction} action
 */
export function* loadPlaylistsSaga(action) {
  try {
    /** @type {Scope[]} */
    const scopes = [Scope.PLAYLIST_READ_PRIVATE]

    /** @type {ReturnType<typeof authorize>} */
    const authorized = yield call(authorize, action, scopes, loadPlaylistsMainSaga)

    yield call(authorized)
  } catch (error) {
    if (error instanceof Error) {
      yield put(showErrorMessage(error.message))
    }
    yield put(loadPlaylistsError())
  }
}

function* loadPlaylistsMainSaga() {
  yield put(loadPlaylistsStart())

  /** @type {RequestWorkers} */
  const { workers, requestChannel, responseChannel, workersFork } = yield call(setupWorkers)
  yield fork(workersFork)

  /** @type {Playlist[]} */
  const playlists = yield call(
    getUserSavedPlaylists,
    requestChannel,
    responseChannel,
    workers.length
  )

  yield cancel(workers)
  yield put(loadPlaylistsFinished(playlists))
}

/**
 * @param {RequestChannel} requestChannel
 * @param {ResponseChannel} responseChannel
 * @param {number} workersCount
 */
function* getUserSavedPlaylists(requestChannel, responseChannel, workersCount) {
  /** @type {ReturnType<typeof getUser>} */
  const user = yield select(getUser)
  if (!user) throw new AuthError('Missing user')

  /** @type {SpotifyPlaylist[]} */
  const spotifyPlaylists = yield call(
    getAllPaged,
    getUserSavedPlaylistsPage,
    requestChannel,
    responseChannel,
    workersCount
  )

  /** @type {Playlist[]} */
  const playlists = []
  /** @type {Set<string>} */
  const ids = new Set()

  for (const playlist of spotifyPlaylists) {
    if (playlist.owner.id !== user.id) continue
    if (ids.has(playlist.id)) continue

    playlists.push({ id: playlist.id, name: playlist.name })
    ids.add(playlist.id)
  }

  return playlists
}
